import {Controller} from "@hotwired/stimulus"

// Sidebar report-argument filters (Linear-style left sidebar).
// One controller per <details> — a lazy-loaded reference-argument group: options
// are fetched from argument_search on first expand (capped server-side via
// &limit=), per-group search re-queries with &q=.
export default class extends Controller {
  static targets = ["list", "search", "count", "status", "rowTemplate"]
  static values = {
    url: String,
    name: String,
    multiple: {type: Boolean, default: true},
    dependent: {type: Boolean, default: false},
    limit: {type: Number, default: 250}
  }

  // server-rendered <details open> doesn't emit a toggle event, so load here
  connect() {
    // data-turbo-permanent moves this DOM across visits — a fresh instance must not refetch an already-loaded list
    this.loaded = !!this.listTarget.querySelector('[data-fetched]')
    this.opened()
  }

  disconnect() {
    clearTimeout(this.searchTimer)
  }

  // <details> toggle — first expand triggers the fetch
  opened() {
    if (this.element.open && !this.loaded) this.load("")
  }

  search() {
    clearTimeout(this.searchTimer)
    const q = this.searchTarget.value.trim()
    this.searchTimer = setTimeout(() => this.load(q), 300)
  }

  load(q) {
    this.loaded = true
    const seq = (this.seq = (this.seq || 0) + 1)
    const url = new URL(this.urlValue, window.location.origin)
    if (q) url.searchParams.set("q", q)
    else url.searchParams.delete("q")
    this.showStatus("Loading…")
    fetch(url, {headers: {Accept: "application/json"}})
      .then(response => response.json())
      .then(json => {
        if (seq === this.seq) this.renderRows(json.items || [], q)
      })
      .catch(() => {
        if (seq === this.seq) this.showStatus("Failed to load")
      })
  }

  renderRows(items, q) {
    // drop rows from the previous fetch unless the user has them checked —
    // keeps the DOM bounded at ~limit rows + selections
    this.listTarget.querySelectorAll('[data-fetched="true"]').forEach(row => {
      const input = row.querySelector("input")
      if (!input || !input.checked) row.remove()
    })

    const existing = new Map()
    this.listTarget.querySelectorAll("input").forEach(input => existing.set(input.value, input))

    const fragment = document.createDocumentFragment()
    items.slice(0, this.limitValue).forEach(item => {
      const id = String(item.id)
      const text = String(item.text ?? item.id)
      if (existing.has(id)) {
        // server-rendered checked rows show the raw id — upgrade to display text
        const span = existing.get(id).closest("label")?.querySelector("span")
        if (span) span.textContent = text
        return
      }
      const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
      row.dataset.fetched = "true"
      const input = row.querySelector("input")
      input.type = this.multipleValue ? "checkbox" : "radio"
      input.name = this.multipleValue ? `${this.nameValue}[]` : this.nameValue
      input.value = id
      existing.set(id, input)
      row.querySelector("span").textContent = text
      fragment.appendChild(row)
    })
    this.listTarget.appendChild(fragment)

    // a linked filter changed since our picks were made — only an unsearched,
    // uncapped list is complete enough to say a pick no longer belongs
    if (this.pruneSelections && !q) {
      this.pruneSelections = false
      if (items.length < this.limitValue) this.dropStalePicks(new Set(items.map(item => String(item.id))))
    }

    if (items.length === 0) {
      this.showStatus(q ? "No matches" : "No options")
    } else if (items.length >= this.limitValue) {
      this.showStatus(`Showing first ${this.limitValue} — type to refine`)
    } else {
      this.hideStatus()
    }
  }

  // group role: an option was (un)checked — refresh the badge and, when this
  // argument drives dependent filters, tell the other groups to rebuild their URLs
  changed() {
    this.announce(this.nameValue)
  }

  // `source` is the group the user actually touched; a cascade of pruned picks
  // carries it along so the chain never loops back to narrow that group
  announce(source) {
    const values = this.selectedValues()
    if (this.hasCountTarget) {
      this.countTarget.textContent = values.length
      this.countTarget.classList.toggle("hidden", values.length === 0)
    }
    if (this.dependentValue) {
      window.dispatchEvent(new CustomEvent("sidebar-filter:arg-changed", {
        detail: {name: this.nameValue, values: values, multiple: this.multipleValue, source: source}
      }))
    }
  }

  // group role: another argument changed — rewrite our fetch URL with its new
  // value(s) and refetch; a group above the changed one can be linked too, and
  // a closed group still refetches when it holds picks that may no longer fit
  siblingChanged(event) {
    if (!this.hasListTarget) return
    const {name, values, multiple, source} = event.detail
    if (name === this.nameValue || source === this.nameValue) return
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.delete(`${name}[]`)
    url.searchParams.delete(name)
    if (multiple) values.forEach(value => url.searchParams.append(`${name}[]`, value))
    else if (values[0]) url.searchParams.set(name, values[0])
    this.urlValue = url.toString()
    this.loaded = false
    this.pruneSource = source || name
    this.pruneSelections = this.selectedValues().length > 0
    if (this.element.open) this.load(this.hasSearchTarget ? this.searchTarget.value.trim() : "")
    else if (this.pruneSelections) this.load("")
  }

  selectedValues() {
    return Array.from(this.listTarget.querySelectorAll("input:checked"))
      .map(input => input.value)
      .filter(value => value !== "")
  }

  // untick picks the narrowed list no longer offers, fall back to "Any" for a
  // single-select, and pass the change on so the report never applies a
  // combination that cannot match
  dropStalePicks(valid) {
    const stale = Array.from(this.listTarget.querySelectorAll("input:checked"))
      .filter(input => input.value !== "" && !valid.has(input.value))
    if (stale.length === 0) return
    stale.forEach(input => (input.closest("label") || input).remove())
    const any = this.listTarget.querySelector('input[value=""]')
    if (any && !this.listTarget.querySelector("input:checked")) any.checked = true
    this.announce(this.pruneSource)
    this.element.dispatchEvent(new Event("change", {bubbles: true}))
  }

  showStatus(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("hidden")
  }

  hideStatus() {
    if (this.hasStatusTarget) this.statusTarget.classList.add("hidden")
  }
}
